import { Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { 
  Bars3Icon,
  BellIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { useResponsive } from '@/contexts/ResponsiveContext'
import useAuthStore from '@/stores/authStore'
import websocketService from '@/services/websocket'

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuthStore()
  const { isMobile, isTablet, isDesktop } = useResponsive()
  const connectionStatus = websocketService.getConnectionStatus()
  
  // Show mobile menu button only on mobile/tablet
  const shouldShowMobileMenu = !isDesktop

  const handleLogout = () => {
    logout()
    websocketService.disconnect()
  }

  return (
    <div className={clsx(
      'relative z-[1040] flex-shrink-0 flex bg-white shadow',
      isMobile ? 'h-14' : 'h-16'
    )}>
      {/* Mobile menu button */}
      {shouldShowMobileMenu && (
        <button
          type="button"
          className={clsx(
            'relative border-r border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors duration-200 touch-manipulation',
            isMobile ? 'px-3 min-w-[48px]' : 'px-4'
          )}
          onClick={(event) => {
            event.stopPropagation()
            onMenuClick()
          }}
          aria-label="Open navigation menu"
        >
          <Bars3Icon className={isMobile ? 'h-6 w-6' : 'h-5 w-5'} aria-hidden="true" />
        </button>
      )}

      <div className={clsx(
        'flex-1 flex justify-between',
        isMobile ? 'px-3' : 'px-4'
      )}>
        {/* Left side - Search or breadcrumbs could go here */}
        <div className="flex-1 flex items-center">
          <div className="flex items-center space-x-2">
            {/* Connection status indicator */}
            <div className="flex items-center space-x-2">
              <div
                className={clsx(
                  'h-2 w-2 rounded-full',
                  connectionStatus.isConnected ? 'bg-green-400' : 'bg-red-400'
                )}
              />
              <span className="text-xs text-gray-500 hidden sm:inline">
                {connectionStatus.isConnected ? '연결됨' : '연결끊김'}
              </span>
            </div>
          </div>
        </div>

        {/* Right side */}
        <div className={clsx(
          'flex items-center',
          isMobile ? 'ml-2 space-x-2' : 'ml-4 md:ml-6 space-x-4'
        )}>
          {/* Notifications - hide on very small screens */}
          {!isMobile && (
            <button
              type="button"
              className="bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 touch-manipulation transition-colors duration-200"
              aria-label="View notifications"
            >
              <BellIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          )}

          {/* Profile dropdown */}
          <Menu as="div" className="relative">
            <div>
              <Menu.Button className={clsx(
                'bg-white flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 touch-manipulation transition-all duration-200',
                isMobile ? 'p-1' : 'p-1'
              )}>
                <span className="sr-only">Open user menu</span>
                <div className={clsx(
                  'rounded-full bg-primary-500 flex items-center justify-center',
                  isMobile ? 'h-9 w-9' : 'h-8 w-8'
                )}>
                  <span className={clsx(
                    'font-medium text-white',
                    isMobile ? 'text-base' : 'text-sm'
                  )}>
                    {user?.nickname?.[0] || user?.username?.[0] || 'A'}
                  </span>
                </div>
              </Menu.Button>
            </div>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {user?.nickname || user?.username}
                  </p>
                  <p className="text-xs text-gray-500">{user?.role}</p>
                </div>
                
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={clsx(
                        active ? 'bg-gray-100' : '',
                        'flex items-center w-full px-4 py-2 text-sm text-gray-700 touch-target'
                      )}
                    >
                      <UserCircleIcon className="mr-3 h-5 w-5 text-gray-400" />
                      프로필
                    </button>
                  )}
                </Menu.Item>
                
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={clsx(
                        active ? 'bg-gray-100' : '',
                        'flex items-center w-full px-4 py-2 text-sm text-gray-700 touch-target'
                      )}
                    >
                      <Cog6ToothIcon className="mr-3 h-5 w-5 text-gray-400" />
                      설정
                    </button>
                  )}
                </Menu.Item>
                
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={handleLogout}
                      className={clsx(
                        active ? 'bg-gray-100' : '',
                        'flex items-center w-full px-4 py-2 text-sm text-gray-700 touch-target'
                      )}
                    >
                      <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-gray-400" />
                      로그아웃
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>
    </div>
  )
}

export default Header